/* eslint-disable no-param-reassign */
import { createApi } from "@reduxjs/toolkit/query/react";
import { identity } from "lodash";

import { sortAgreementObjectsByNameAsc } from "helpers/agreements";
import { IAgreementObject } from "models/agreements";
import { RevisionStatus } from "models/revisions";
import { AppConfigs } from "config/appConfigs";
import { environmentConfig } from "config/environmentConfig";
import { AuthClientProvider } from "./client";
import { azureDevOpsBaseQuery } from "./azureDevOpsBaseQuery";

export interface IPolicy {
  id: string;
  name: string;
  description: string;
  template: string;
  status: RevisionStatus;
}

/**
 * Get the current user's Azure DevOps identity ID
 * @param fetchWithBQ - The fetch function from RTK Query
 * @returns The current user's Azure DevOps identity ID
 */
const getCurrentUserAzureDevOpsId = async (fetchWithBQ: any): Promise<{ data?: string; error?: any }> => {
  // Use absolute URL for organization-level API (fetchWithBQ will add auth headers)
  const organizationUrl = 'https://dev.azure.com/microsoft/_apis/connectionData?api-version=7.1-preview';
  
  const result = await fetchWithBQ(organizationUrl);
  
  if (result.error) {
    return { error: result.error };
  }
  
  const connectionData = result.data;
  const userId = connectionData?.authenticatedUser?.id;
  
  if (!userId) {
    return { error: { status: 500, data: 'Could not retrieve user identity' } };
  }
  
  return { data: userId };
};

/**
 * Check if a branch exists in the repository
 * @param fetchWithBQ - The fetch function from RTK Query
 * @param branchName - The branch name to check
 * @returns True if branch exists, false otherwise
 */
const branchExists = async (
  fetchWithBQ: any,
  branchName: string
): Promise<boolean> => {
  const refsResult = await fetchWithBQ(`refs?filter=heads/${branchName}&api-version=7.0`);
  
  if (refsResult.error) {
    return false;
  }
  
  const refsData = refsResult.data as {
    value: Array<{ name: string; objectId: string }>;
  };
  
  return refsData.value && refsData.value.length > 0;
};

/**
 * Check if an active PR exists for a source branch
 * @param fetchWithBQ - The fetch function from RTK Query
 * @param sourceBranch - The source branch name
 * @param targetBranch - The target branch name (default: 'main')
 * @returns PR ID if exists, null otherwise
 */
const getActivePR = async (
  fetchWithBQ: any,
  sourceBranch: string,
  targetBranch: string = 'main'
): Promise<number | null> => {
  const prResult = await fetchWithBQ(
    `pullrequests?searchCriteria.sourceRefName=refs/heads/${sourceBranch}&searchCriteria.targetRefName=refs/heads/${targetBranch}&searchCriteria.status=active&api-version=7.0`
  );
  
  if (prResult.error || !prResult.data) {
    return null;
  }
  
  const prData = prResult.data as { value: Array<{ pullRequestId: number }> };
  
  if (prData.value && prData.value.length > 0) {
    return prData.value[0].pullRequestId;
  }
  
  return null;
};

/**
 * Get the latest commit from the main branch
 * @param fetchWithBQ - The fetch function from RTK Query
 * @param branchName - The branch to get the latest commit from (default: 'main')
 * @returns The latest commit objectId or error
 */
const getLatestCommitFromMain = async (
  fetchWithBQ: any,
  branchName: string = 'main'
): Promise<{ data?: string; error?: any }> => {
  const url = `refs?filter=heads/${branchName}&api-version=7.0`;

  const refsResult = await fetchWithBQ(url);


  if (refsResult.error) {
    return { error: refsResult.error };
  }

  const refsData = refsResult.data as {
    value: Array<{ name: string; objectId: string }>;
  };

  if (!refsData.value || refsData.value.length === 0) {
    return {
      error: {
        status: 404,
        data: `Branch '${branchName}' not found`
      } as any
    };
  }

  return { data: refsData.value[0].objectId };
};

/**
 * Create a pull request in Azure DevOps
 * @param fetchWithBQ - The fetch function from RTK Query
 * @param sourceBranch - The source branch for the PR
 * @param title - The title of the PR
 * @param description - The description of the PR
 * @param targetBranch - The target branch for the PR (default: 'main')
 * @param isDraft - Whether the PR is a draft (default: false)
 * @returns The pull request result or error
 */
const createPullRequest = async (
  fetchWithBQ: any,
  sourceBranch: string,
  title: string,
  description: string,
  targetBranch: string = 'main',
  isDraft: boolean = false
): Promise<{ data?: any; error?: any }> => {
  // Create the Pull Request
  const url = `pullrequests?api-version=7.0`;
  const requestBody = {
    sourceRefName: `refs/heads/${sourceBranch}`,
    targetRefName: `refs/heads/${targetBranch}`,
    title,
    description,
    isDraft,
  };

  const prResult = await fetchWithBQ({
    url,
    method: "POST",
    body: requestBody
  });


  if (prResult.error) {
    return { error: prResult.error };
  }

  return { data: prResult.data };
};

/**
 * Enable auto-complete on a pull request
 * @param fetchWithBQ - The fetch function from RTK Query
 * @param pullRequestId - The ID of the pull request
 * @param currentUser - The current user ID for auto-complete
 * @param title - The merge commit message
 * @param deleteSourceBranch - Whether to delete the source branch after merge (default: true)
 * @param mergeStrategy - The merge strategy to use (default: 'squash')
 * @returns The auto-complete result or error
 */
const enablePullRequestAutoComplete = async (
  fetchWithBQ: any,
  pullRequestId: number,
  currentUser: string,
  title: string,
  deleteSourceBranch: boolean = true,
  mergeStrategy: string = 'squash'
): Promise<{ data?: any; error?: any }> => {
  const url = `pullrequests/${pullRequestId}?api-version=7.1`;
  const requestBody = {
    autoCompleteSetBy: {
      id: currentUser
    },
    completionOptions: {
      deleteSourceBranch,
      mergeCommitMessage: title,
      transitionWorkItems: true,
      mergeStrategy
    }
  };

  const autoCompleteResult = await fetchWithBQ({
    url,
    method: "PATCH",
    body: requestBody
  });

  if (autoCompleteResult.error) {
    return { error: autoCompleteResult.error };
  }

  return { data: autoCompleteResult.data };
};

export const policyApi = createApi({
  reducerPath: "policyApi",
  baseQuery: azureDevOpsBaseQuery(),
  tagTypes: ["PolicyList", "Policy"],
  endpoints: (builder) => ({
    getAll: builder.query<IAgreementObject[], { cloud?: string; environment?: string; policyType?: string } | void>({
      queryFn: async (args, _queryApi, _extraOptions, fetchWithBQ) => {
        const params = (args || {}) as { cloud?: string; environment?: string; policyType?: string };
        const cloud = params.cloud || environmentConfig.cloud;
        const environment = params.environment || environmentConfig.environment;
        const policyType = params.policyType || "ImplicitAcceptance";

        const scopePath = `/${cloud}/${environment}/${policyType}`;

        type PolicyData = {
          id?: string;
          name?: string;
          type?: string;
          description?: string;
          status?: RevisionStatus;
          templateName?: string;
          templateId?: string;
          constraints?: unknown[];
          assemblyOptions?: unknown;
          languageLocale?: unknown;
          targetScope?: {
            templateId?: string;
            templateName?: string;
            contentRefreshDate?: string;
          };
          agreementPayload?: {
            startEffectiveDate?: string;
          };
          execution?: {
            jobStartDate?: string;
          };
          isTestPolicy?: boolean;
          createdDate?: string;
          modifiedDate?: string;
          createdBy?: string;
          modifiedBy?: string;
        };

        // Step 1: Get all policies from main branch
        const mainItemsUrl = `items?scopePath=${scopePath}&recursionLevel=oneLevel&versionDescriptor.version=main&versionDescriptor.versionType=branch&api-version=7.0`;
        const mainItemsResult = await fetchWithBQ(mainItemsUrl);

        if (mainItemsResult.error) {
          return { error: mainItemsResult.error };
        }

        const mainItemsData = mainItemsResult.data as {
          count: number;
          value: Array<{
            objectId: string;
            gitObjectType: string;
            commitId: string;
            path: string;
            isFolder?: boolean;
            url: string;
          }>;
        };

        // Fetch all main policies in parallel
        const mainPolicyPromises = mainItemsData.value
          .filter(item => item.gitObjectType === "blob")
          .map(async (item) => {
            const policyDetailsUrl = `items?path=${item.path}&versionDescriptor.version=main&versionDescriptor.versionType=branch&api-version=7.0`;
            const policyDetailsResult = await fetchWithBQ(policyDetailsUrl);

            if (policyDetailsResult.error) {
              return null;
            }

            return policyDetailsResult.data as PolicyData;
          });

        const mainPolicies = (await Promise.all(mainPolicyPromises)).filter(p => p && p.id);

        // Create a map with main policies
        const policyMap = new Map<string, PolicyData>();
        mainPolicies.forEach(policy => {
          if (policy?.id) {
            policyMap.set(policy.id, policy);
          }
        });

        // Step 2: Get all branches and filter for policy branches
        const branchesUrl = "refs?filter=heads/policy/&api-version=7.0";
        const branchesResult = await fetchWithBQ(branchesUrl);

        if (branchesResult.error) {
          // Continue with just main policies if branch fetch fails
        } else {
          const branchesData = branchesResult.data as {
            value: Array<{ name: string; objectId: string }>;
          };

          // Step 3: For each policy branch, fetch the specific policy file
          const policyBranchPromises = branchesData.value.map(async (branch) => {
            const branchName = branch.name.replace("refs/heads/", "");
            const policyId = branchName.replace("policy/", "");
            
            // Construct the file path for this policy (only replace spaces, not dashes)
            const fileName = `${policyId.replace(/\s+/g, '_')}.json`;
            const filePath = `${scopePath}/${fileName}`;

            const policyDetailsUrl = `items?path=${filePath}&versionDescriptor.version=${branchName}&versionDescriptor.versionType=branch&api-version=7.0`;
            const policyDetailsResult = await fetchWithBQ(policyDetailsUrl);

            if (policyDetailsResult.error) {
              // File not found in branch - policy was deleted, mark for removal
              return { policyId, isDeleted: true };
            }

            return { policyId, policy: policyDetailsResult.data as PolicyData, isDeleted: false };
          });

          const policyBranchResults = await Promise.all(policyBranchPromises);

          // Step 4: Process policy branches - replace or add policies, remove deleted ones
          policyBranchResults.forEach(result => {
            if (result.isDeleted) {
              // Policy deleted in branch - remove from map
              policyMap.delete(result.policyId);
            } else if (result.policy?.id) {
              // Policy exists in branch - add or replace
              policyMap.set(result.policy.id, result.policy);
            }
          });
        }

        // Map all policies to agreementObjects
        const agreementObjects: IAgreementObject[] = Array.from(policyMap.values())
          .map((policyData) => ({
            key: policyData.id as string,
            name: policyData.name || "",
            type: policyData.type || "",
            description: policyData.description || "",
            status: policyData.status || "",
            template: policyData.targetScope?.templateName || "",
            templateId: policyData.targetScope?.templateId || "",
            contentRefreshDate: policyData.targetScope?.contentRefreshDate || "",
            agreementEffectiveDate: policyData.agreementPayload?.startEffectiveDate || "",
            executionDate: policyData.execution?.jobStartDate || "",
            createdDate: policyData.createdDate || "",
            modifiedDate: policyData.modifiedDate || "",
            createdBy: policyData.createdBy || "",
            modifiedBy: policyData.modifiedBy || "",
            isTestPolicy: policyData?.isTestPolicy || false,
            objectIdInfo: { policyId: policyData.id as string },
          }))
          .filter((obj) => obj.key && obj.key !== 'undefined' && obj.name);

        sortAgreementObjectsByNameAsc(agreementObjects);
        return {
          data: agreementObjects,
        };
      },
      providesTags: ["PolicyList"],
    }),

    getPolicyDetails: builder.query<
      {
        id: string;
        name: string;
        description: string;
        type: string;
        status: string;
        template?: string;
        templateId?: string;
        criteria?: Array<{
          filterType: string;
          source: string;
          query: string;
        }>;
        contentRefreshDate?: string;
        agreementEffectiveDate?: string;
        agreementSettings?: 'entire' | 'updated';
        isTestPolicy?: boolean;
        constraints?: any;
      },
      {
        id: string;
        cloud?: string;
        environment?: string;
        policyType?: string;
      }
    >({
      queryFn: async (args, _queryApi, _extraOptions, fetchWithBQ) => {
        const { id, cloud, environment, policyType } = args;

        // Construct the file path
        const normalizedPolicyType = policyType?.replace(/\s+/g, '');
        const actualCloud = cloud || environmentConfig.cloud;
        const actualEnvironment = environment || environmentConfig.environment;
        const scopePath = `/${actualCloud}/${actualEnvironment}/${normalizedPolicyType}`;
        const fileName = `${identity(id).replace(/\s+/g, '_')}.json`;
        const filePath = `${scopePath}/${fileName}`;

        // Check if policy branch exists
        const policyFeatureBranch = `policy/${id.replace(/\s+/g, '-').toLowerCase()}`;
        const policyBranchExists = await branchExists(fetchWithBQ, policyFeatureBranch);
        
        let policyResult;
        
        if (policyBranchExists) {
          // Policy branch exists - try to get policy from it
          policyResult = await fetchWithBQ(
            `items?path=${filePath}&versionDescriptor.version=${policyFeatureBranch}&versionDescriptor.versionType=branch&api-version=7.0`
          );
          
          // If policy deleted in branch (404), return error - don't fall back to main
          if (policyResult.error) {
            return { 
              error: {
                status: 404,
                data: 'Policy has been deleted'
              } as any
            };
          }
        } else {
          // No policy branch - get from main
          policyResult = await fetchWithBQ(
            `items?path=${filePath}&versionDescriptor.version=main&versionDescriptor.versionType=branch&api-version=7.0`
          );
          
          if (policyResult.error) {
            return { error: policyResult.error };
          }
        }

        const policyData = policyResult.data as any;

        // Get template name - either from policy data or fetch from template service
        let templateName = policyData.targetScope?.templateName;
        const templateId = policyData.targetScope?.templateId;
        
        // If template name is missing but templateId exists, fetch it from template service
        if (!templateName && templateId) {
          try {
            const authClient = AuthClientProvider.getInstance();
            const { scopeTemplateService } = AppConfigs().authentication;
            const token = await authClient.getAuthenticationToken(scopeTemplateService);
            const { templateServiceBaseUrl } = AppConfigs();
            
            const response = await fetch(`${templateServiceBaseUrl}/template/${templateId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (response.ok) {
              const templateData = await response.json();
              templateName = templateData.name;
            }
          } catch (error) {
            // Failed to fetch template name
          }
        }

        const responseData = {
          id: policyData.id || id,
          name: policyData.name || "",
          description: policyData.description || "",
          type: policyData.type || "",
          status: policyData.status || "",
          template: templateName,
          templateId,
          criteria: policyData.targetScope?.criteria || [],
          // Include publish-specific fields for pre-population when re-publishing
          contentRefreshDate: policyData.targetScope?.contentRefreshDate,
          agreementEffectiveDate: policyData.agreementPayload?.startEffectiveDate,
          agreementSettings: (policyData.agreementPayload?.assemblyOptions?.Delta === true ? 'updated' : 'entire') as 'entire' | 'updated' | undefined,
          isTestPolicy: policyData.isTestPolicy || false,
          constraints: policyData.agreementPayload?.constraints,
        };
        return {
          data: responseData
        };
      },
      providesTags: (result, error, args) =>
        result ? [{ type: "Policy", id: args.id }] : ["Policy"],
    }),

    getPolicy: builder.query<IPolicy, string | undefined>({
      queryFn: async (id) => {
        // TODO: Replace with actual API call
        // const result = await fetchWithBQ(`policy/${id}`);

        // Mock data for now
        const mockPolicy: IPolicy = {
          id: id || "",
          name: `Policy ${id}`,
          description: `Description for policy ${id}`,
          template: "General",
          status: RevisionStatus.Live,
        };

        return { data: mockPolicy };
      },
      providesTags: (result) =>
        result ? [{ type: "Policy", id: result!.id }] : ["Policy"],
    }),

    createPolicy: builder.mutation<
      { success: boolean; policyId: string },
      {
        id: string;
        type: string;
        name: string;
        description: string;
        status: string;
        cloud?: string;
        environment?: string;
        policyType?: string;
      }
    >({
      queryFn: async (args, _queryApi, _extraOptions, fetchWithBQ) => {
        const {
          id,
          type,
          name,
          description,
          status,
          cloud,
          environment,
          policyType
        } = args;

        // Get the current logged-in user
        const authClient = AuthClientProvider.getInstance();
        const currentUser = authClient.getCurrentUser();

        // Construct the file path based on cloud/environment/policyType
        // Remove spaces from policyType for file path
        const normalizedPolicyType = policyType?.replace(/\s+/g, '');
        const actualCloud = cloud || environmentConfig.cloud;
        const actualEnvironment = environment || environmentConfig.environment;
        const scopePath = `/${actualCloud}/${actualEnvironment}/${normalizedPolicyType}`;
        const fileName = `${identity(id).replace(/\s+/g, '_')}.json`;
        const filePath = `${scopePath}/${fileName}`;

        // Create the policy data as raw JSON text
        const currentDate = new Date().toISOString();
        // Set isTest based on environment: false for prod, true for all other environments
        // const isTestPolicy = environment?.toLowerCase() !== 'prod';
        const policyData = {
          id,
          type,
          name,
          description,
          status,
          // isTestPolicy,
          createdDate: currentDate,
          modifiedDate: currentDate,
          createdBy: currentUser,
          modifiedBy: currentUser
        };

        const jsonContent = JSON.stringify(policyData, null, 2);

        const featureBranch = `policy/${id.replace(/\s+/g, '-').toLowerCase()}`;

        // Check if policy branch already exists
        const policyBranchExists = await branchExists(fetchWithBQ, featureBranch);
        
        // Determine source branch: use policy branch if it exists, otherwise main
        const sourceBranch = policyBranchExists ? featureBranch : 'main';
        
        const commitResult = await getLatestCommitFromMain(fetchWithBQ, sourceBranch);

        if (commitResult.error) {
          return { error: commitResult.error };
        }

        const latestCommitId = commitResult.data!;

        // Create or update the policy branch
        const createResult = await fetchWithBQ({
          url: "pushes?api-version=7.0",
          method: "POST",
          body: {
            refUpdates: [
              {
                name: `refs/heads/${featureBranch}`,
                oldObjectId: latestCommitId
              }
            ],
            commits: [
              {
                comment: `Create policy: ${name}`,
                changes: [
                  {
                    changeType: "add",
                    item: {
                      path: filePath
                    },
                    newContent: {
                      content: jsonContent,
                      contentType: "rawtext"
                    }
                  }
                ]
              }
            ]
          }
        });


        if (createResult.error) {
          return { error: createResult.error };
        }

        const responseData = {
          success: true,
          policyId: id
        };
        return {
          data: responseData
        };
      },
      invalidatesTags: ["PolicyList"],
    }),

    updatePolicy: builder.mutation<
      { success: boolean; policyId: string },
      {
        id: string;
        templateId?: string | null;
        templateName?: string | null;
        status?: string;
        cloud?: string;
        environment?: string;
        policyType?: string;
      }
    >({
      queryFn: async (args, _queryApi, _extraOptions, fetchWithBQ) => {
        const {
          id,
          templateId,
          templateName,
          status,
          cloud,
          environment,
          policyType
        } = args;

        // Get the current logged-in user
        const authClient = AuthClientProvider.getInstance();
        const currentUser = authClient.getCurrentUser();

        // Construct the file path based on cloud/environment/policyType
        // Remove spaces from policyType for file path
        const normalizedPolicyType = policyType?.replace(/\s+/g, '');
        const actualCloud = cloud || environmentConfig.cloud;
        const actualEnvironment = environment || environmentConfig.environment;
        const scopePath = `/${actualCloud}/${actualEnvironment}/${normalizedPolicyType}`;
        const fileName = `${identity(id).replace(/\s+/g, '_')}.json`;
        const filePath = `${scopePath}/${fileName}`;

        // Get the policy's feature branch
        const policyFeatureBranch = `policy/${id.replace(/\s+/g, '-').toLowerCase()}`;

        // Check if policy branch exists
        const policyBranchExists = await branchExists(fetchWithBQ, policyFeatureBranch);
        
        // Try policy branch first, fallback to main
        let currentPolicyResult = await fetchWithBQ(
          `items?path=${filePath}&versionDescriptor.version=${policyFeatureBranch}&versionDescriptor.versionType=branch&api-version=7.0`
        );

        // If policy branch doesn't exist, get from main
        if (currentPolicyResult.error) {
          currentPolicyResult = await fetchWithBQ(
            `items?path=${filePath}&versionDescriptor.version=main&versionDescriptor.versionType=branch&api-version=7.0`
          );
          
          if (currentPolicyResult.error) {
            return { error: currentPolicyResult.error };
          }
        }

        const currentPolicyData = currentPolicyResult.data as any;

        // Enforce rule: Cannot edit published policies without withdrawing first
        if (currentPolicyData.status === 'Published' && status !== 'Draft') {
          return {
            error: {
              status: 400,
              data: 'Cannot edit published policies. Please withdraw the policy first.'
            } as any
          };
        }

        // Update the policy data with new template info and status
        // Handle null values to clear template, undefined to keep current value
        const updatedPolicyData = {
          ...currentPolicyData,
          ...(status !== undefined && { status }),
          ...(templateId === null 
            ? {} // Remove targetScope entirely when clearing template
            : {
                targetScope: {
                  ...currentPolicyData.targetScope,
                  templateId: templateId !== undefined ? templateId : currentPolicyData.targetScope?.templateId,
                  templateName: templateName !== undefined ? templateName : currentPolicyData.targetScope?.templateName,
                }
              }
          ),
          modifiedDate: new Date().toISOString(),
          modifiedBy: currentUser
        };

        // Remove targetScope property if template is being cleared
        if (templateId === null) {
          delete updatedPolicyData.targetScope;
          
          // Also remove template-related fields from agreementPayload
          if (updatedPolicyData.agreementPayload) {
            delete updatedPolicyData.agreementPayload.templateId;
            delete updatedPolicyData.agreementPayload.templateName;
            delete updatedPolicyData.agreementPayload.constraints;
          }
        }

        const jsonContent = JSON.stringify(updatedPolicyData, null, 2);

        // Get source branch for creating/updating policy branch
        const sourceBranch = policyBranchExists ? policyFeatureBranch : 'main';
        const commitResult = await getLatestCommitFromMain(fetchWithBQ, sourceBranch);

        if (commitResult.error) {
          return { error: commitResult.error };
        }

        const latestCommitId = commitResult.data!;

        // Update the file in Azure DevOps using the Push API
        const updatePushUrl = "pushes?api-version=7.0";
        const updatePushBody = {
          refUpdates: [
            {
              name: `refs/heads/${policyFeatureBranch}`,
              oldObjectId: latestCommitId
            }
          ],
          commits: [
            {
              comment: `Update policy: ${updatedPolicyData.name} - Add template ${templateName}`,
              changes: [
                {
                  changeType: "edit",
                  item: {
                    path: filePath
                  },
                  newContent: {
                    content: jsonContent,
                    contentType: "rawtext"
                  }
                }
              ]
            }
          ]
        };

        const updateResult = await fetchWithBQ({
          url: updatePushUrl,
          method: "POST",
          body: updatePushBody
        });


        if (updateResult.error) {
          return { error: updateResult.error };
        }

        const responseData = {
          success: true,
          policyId: id
        };
        return {
          data: responseData
        };
      },
      invalidatesTags: ["PolicyList"],
    }),

    updatePolicyCriteria: builder.mutation<
      { success: boolean; policyId: string },
      {
        id: string;
        templateId?: string;
        templateName?: string;
        criteria: Array<{
          filterType: string;
          source: string;
          query: string;
        }>;
        cloud?: string;
        environment?: string;
        policyType?: string;
      }
    >({
      queryFn: async (args, _queryApi, _extraOptions, fetchWithBQ) => {
        const {
          id,
          templateId,
          templateName,
          criteria,
          cloud,
          environment,
          policyType
        } = args;

        // Get the current logged-in user
        const authClient = AuthClientProvider.getInstance();
        const currentUser = authClient.getCurrentUser();

        // Construct the file path based on cloud/environment/policyType
        const normalizedPolicyType = policyType?.replace(/\s+/g, '');
        const actualCloud = cloud || environmentConfig.cloud;
        const actualEnvironment = environment || environmentConfig.environment;
        const scopePath = `/${actualCloud}/${actualEnvironment}/${normalizedPolicyType}`;
        const fileName = `${identity(id).replace(/\s+/g, '_')}.json`;
        const filePath = `${scopePath}/${fileName}`;

        // Get the policy's feature branch
        const policyFeatureBranch = `policy/${id.replace(/\s+/g, '-').toLowerCase()}`;

        // Check if policy branch exists
        const policyBranchExists = await branchExists(fetchWithBQ, policyFeatureBranch);
        
        // Try policy branch first, fallback to main
        let currentPolicyResult = await fetchWithBQ(
          `items?path=${filePath}&versionDescriptor.version=${policyFeatureBranch}&versionDescriptor.versionType=branch&api-version=7.0`
        );

        // If policy branch doesn't exist, get from main
        if (currentPolicyResult.error) {
          currentPolicyResult = await fetchWithBQ(
            `items?path=${filePath}&versionDescriptor.version=main&versionDescriptor.versionType=branch&api-version=7.0`
          );
          
          if (currentPolicyResult.error) {
            return { error: currentPolicyResult.error };
          }
        }

        const currentPolicyData = currentPolicyResult.data as any;

        // Enforce rule: Cannot edit published policies without withdrawing first
        if (currentPolicyData.status === 'Published') {
          return {
            error: {
              status: 400,
              data: 'Cannot edit published policies. Please withdraw the policy first.'
            } as any
          };
        }

        // Update the policy data with criteria
        const updatedPolicyData = {
          ...currentPolicyData,
          targetScope: {
            ...currentPolicyData.targetScope,
            templateId: templateId || currentPolicyData.targetScope?.templateId,
            templateName: templateName || currentPolicyData.targetScope?.templateName,
            criteria,
          },
          modifiedDate: new Date().toISOString(),
          modifiedBy: currentUser
        };

        const jsonContent = JSON.stringify(updatedPolicyData, null, 2);

        // Get source branch for creating/updating policy branch
        const sourceBranch = policyBranchExists ? policyFeatureBranch : 'main';
        const commitResult = await getLatestCommitFromMain(fetchWithBQ, sourceBranch);

        if (commitResult.error) {
          return { error: commitResult.error };
        }

        const latestCommitId = commitResult.data!;

        // Update the file in Azure DevOps using the Push API
        const updateCriteriaPushUrl = "pushes?api-version=7.0";
        const updateCriteriaPushBody = {
          refUpdates: [
            {
              name: `refs/heads/${policyFeatureBranch}`,
              oldObjectId: latestCommitId
            }
          ],
          commits: [
            {
              comment: `Update policy: ${updatedPolicyData.name} - Update criteria`,
              changes: [
                {
                  changeType: "edit",
                  item: {
                    path: filePath
                  },
                  newContent: {
                    content: jsonContent,
                    contentType: "rawtext"
                  }
                }
              ]
            }
          ]
        };

        const updateResult = await fetchWithBQ({
          url: updateCriteriaPushUrl,
          method: "POST",
          body: updateCriteriaPushBody
        });


        if (updateResult.error) {
          return { error: updateResult.error };
        }

        const responseData = {
          success: true,
          policyId: id
        };
        return {
          data: responseData
        };
      },
      invalidatesTags: ["PolicyList"],
    }),

    deletePolicy: builder.mutation<
      { success: boolean },
      {
        id: string;
        status: string;
        cloud?: string;
        environment?: string;
        policyType?: string;
      }
    >({
      queryFn: async (args, _queryApi, _extraOptions, fetchWithBQ) => {
        const {
          id,
          status,
          cloud,
          environment,
          policyType
        } = args;

        // Only allow deletion if status is Draft
        if (status !== RevisionStatus.Draft) {
          return {
            error: {
              status: 400,
              data: 'Only policies with Draft status can be deleted'
            } as any
          };
        }

        // Construct the file path
        const normalizedPolicyType = policyType?.replace(/\s+/g, '');
        const actualCloud = cloud || environmentConfig.cloud;
        const actualEnvironment = environment || environmentConfig.environment;
        const scopePath = `/${actualCloud}/${actualEnvironment}/${normalizedPolicyType}`;
        const fileName = `${identity(id).replace(/\s+/g, '_')}.json`;
        const filePath = `${scopePath}/${fileName}`;

        // Get the policy's feature branch
        const policyFeatureBranch = `policy/${id.replace(/\s+/g, '-').toLowerCase()}`;

        // Check if policy branch exists
        const policyBranchExists = await branchExists(fetchWithBQ, policyFeatureBranch);
        
        // Determine source branch: use policy branch if exists, otherwise main
        const sourceBranch = policyBranchExists ? policyFeatureBranch : 'main';
        
        // Get the latest commit from the source branch
        const commitResult = await getLatestCommitFromMain(fetchWithBQ, sourceBranch);

        if (commitResult.error) {
          return { error: commitResult.error };
        }

        const latestCommitId = commitResult.data!;

        // Delete the file using Azure DevOps Push API
        const deletePushUrl = "pushes?api-version=7.0";
        const deletePushBody = {
          refUpdates: [
            {
              name: `refs/heads/${policyFeatureBranch}`,
              oldObjectId: latestCommitId
            }
          ],
          commits: [
            {
              comment: `Delete draft policy: ${id}`,
              changes: [
                {
                  changeType: "delete",
                  item: {
                    path: filePath
                  }
                }
              ]
            }
          ]
        };

        const deleteResult = await fetchWithBQ({
          url: deletePushUrl,
          method: "POST",
          body: deletePushBody
        });


        if (deleteResult.error) {
          return { error: deleteResult.error };
        }

        // Check if the policy file exists in main branch
        const mainFileResult = await fetchWithBQ(
          `items?path=${filePath}&versionDescriptor.version=main&versionDescriptor.versionType=branch&api-version=7.0`
        );

        const fileExistsInMain = !mainFileResult.error;

        // Only create PR if the file exists in main (i.e., policy was published at some point)
        if (fileExistsInMain) {
          // Get the current user's Azure DevOps ID for PR auto-complete
          const userIdResult = await getCurrentUserAzureDevOpsId(fetchWithBQ);
          
          if (userIdResult.error) {
            return { error: userIdResult.error };
          }
          
          const currentUserId = userIdResult.data!;

          // Check if a PR already exists for this branch
          const existingPRId = await getActivePR(fetchWithBQ, policyFeatureBranch, 'main');
          
          if (existingPRId) {
            // Use existing PR
          } else {
            // Create a new Pull Request to merge the delete to main
            const prResult = await createPullRequest(
              fetchWithBQ,
              policyFeatureBranch,
              `Delete draft policy: ${id}`,
              `This PR deletes the draft policy file for policy ID: ${id}`,
              'main',
              false
            );

            if (prResult.error) {
              return { error: prResult.error };
            }

            const pullRequestId = prResult.data.pullRequestId as number;

            // Enable auto-complete on the new PR
            const autoCompleteResult = await enablePullRequestAutoComplete(
              fetchWithBQ,
              pullRequestId,
              currentUserId,
              `Delete draft policy: ${id}`,
              true,
              'squash'
            );

            if (autoCompleteResult.error) {
              // Don't fail the whole operation if auto-complete fails
            }
          }
        } else {
          // Policy never published - only exists on feature branch
          // Delete the feature branch directly (no PR needed)
          
          // Note: Branch will be deleted after file deletion commit above
          // No PR needed as there's nothing to merge to main
        }

        const responseData = {
          success: true
        };
        return {
          data: responseData
        };
      },
      invalidatesTags: ["PolicyList"],
    }),

    publishPolicy: builder.mutation<
      { success: boolean; policyId: string; pullRequestId: number },
      {
        id: string;
        contentRefreshDate: string;
        agreementEffectiveDate: string;
        agreementSettings: 'entire' | 'updated';
        isInternalTestOnly: boolean;
        cloud?: string;
        environment?: string;
        policyType?: string;
        constraints?: Array<{ propertyKey: string; propertySource: string; propertyValue: null }>;
      }
    >({
      queryFn: async (args, _queryApi, _extraOptions, fetchWithBQ) => {
        const {
          id,
          contentRefreshDate,
          agreementEffectiveDate,
          agreementSettings,
          isInternalTestOnly,
          cloud, 
          environment, 
          policyType,
          constraints
        } = args;

        // Get the current logged-in user
        const authClient = AuthClientProvider.getInstance();
        const currentUser = authClient.getCurrentUser();
        
        // Get Azure DevOps user ID for PR auto-complete
        const userIdResult = await getCurrentUserAzureDevOpsId(fetchWithBQ);
        
        if (userIdResult.error) {
          return { error: userIdResult.error };
        }
        
        const currentUserId = userIdResult.data!;

        // Construct the file path based on cloud/environment/policyType
        const normalizedPolicyType = policyType?.replace(/\s+/g, '');
        const actualCloud = cloud || environmentConfig.cloud;
        const actualEnvironment = environment || environmentConfig.environment;
        const scopePath = `/${actualCloud}/${actualEnvironment}/${normalizedPolicyType}`;
        const fileName = `${identity(id).replace(/\s+/g, '_')}.json`;
        const filePath = `${scopePath}/${fileName}`;

        // Get the policy's feature branch
        const policyFeatureBranch = `policy/${id.replace(/\s+/g, '-').toLowerCase()}`;

        // Check if policy branch exists
        const policyBranchExists = await branchExists(fetchWithBQ, policyFeatureBranch);
        
        // Try policy branch first, fallback to main
        let currentPolicyResult = await fetchWithBQ(
          `items?path=${filePath}&versionDescriptor.version=${policyFeatureBranch}&versionDescriptor.versionType=branch&api-version=7.0`
        );

        // If policy branch doesn't exist, get from main
        if (currentPolicyResult.error) {
          currentPolicyResult = await fetchWithBQ(
            `items?path=${filePath}&versionDescriptor.version=main&versionDescriptor.versionType=branch&api-version=7.0`
          );
          
          if (currentPolicyResult.error) {
            return { error: currentPolicyResult.error };
          }
        }

        const currentPolicyData = currentPolicyResult.data as any;
        const currentDate = new Date().toISOString();
        const templateId = currentPolicyData.targetScope?.templateId;

        // Fetch the latest published revision for the template
        let latestPublishedRevisionId = null;
        const requiredConstraints: any = {};

        if (templateId) {
          try {
            // Get authentication token and base URL for template service
            const token = await authClient.getAuthenticationToken(
              AppConfigs().authentication.scopeTemplateService
            );
            const baseUrl = AppConfigs().templateServiceBaseUrl;
            const headers = {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            };

            // Get all revisions for the template
            const revisionsResponse = await fetch(
              `${baseUrl}/template/${templateId}/revisions`,
              { headers }
            );

            if (revisionsResponse.ok) {
              const revisions = await revisionsResponse.json() as any[];
              // Find the latest published revision (status = "Published" or "Live")
              const publishedRevisions = revisions.filter(
                (rev: any) => rev.status === "Published" || rev.status === "Live"
              );
              
              if (publishedRevisions.length > 0) {
                // Sort by effectiveDate descending to get the latest
                publishedRevisions.sort((a: any, b: any) => 
                  new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()
                );
                latestPublishedRevisionId = publishedRevisions[0].id;

                // Fetch required constraints for this template and revision
                const constraintsResponse = await fetch(
                  `${baseUrl}/template/${templateId}/revision/${latestPublishedRevisionId}/requiredconstraints`,
                  { headers }
                );

                if (constraintsResponse.ok) {
                  const templateConstraints = await constraintsResponse.json() as any[];
                  // Build constraints object with display name as key
                  templateConstraints.forEach((constraint: any) => {
                    const displayName = constraint.constraintKeyDisplay || constraint.constraintKeyName;
                    requiredConstraints[displayName] = `$.Agreement.Constraints.${displayName}`;
                  });
                }
              }
            }
          } catch (error) {
            // Failed to fetch template revisions or constraints
          }
        }

        // Determine if this is a delta (updated terms only) or full agreement
        const isDelta = agreementSettings === 'updated';

        // Transform constraints from array to object format if needed
        let constraintsObject = requiredConstraints;
        if (Object.keys(requiredConstraints).length === 0) {
          if (constraints && Array.isArray(constraints)) {
            // Convert array format to object format
            constraintsObject = constraints.reduce((acc, constraint) => {
              acc[constraint.propertyKey] = `$.Agreement.Constraints.${constraint.propertyKey}`;
              return acc;
            }, {} as any);
          } else {
            constraintsObject = currentPolicyData.agreementPayload?.constraints || {};
          }
        }

        // Update KQL queries in criteria to add/update StartEffectiveDate filter
        const updatedCriteria = (currentPolicyData.targetScope?.criteria || []).map((criterion: any) => {
          if (criterion.filterType === 'KQL' && criterion.query) {
            let updatedQuery = criterion.query;
            
            // Remove existing StartEffectiveDate filter if present (to replace with new date)
            updatedQuery = updatedQuery.replace(/\s+and\s+StartEffectiveDate\s*<\s*datetime\('[^']*'\)/g, '');
            
            // Add new StartEffectiveDate filter before the summarize clause
            const summarizeIndex = updatedQuery.indexOf('| summarize');
            if (summarizeIndex !== -1) {
              const beforeSummarize = updatedQuery.substring(0, summarizeIndex);
              const summarizeAndAfter = updatedQuery.substring(summarizeIndex);
              updatedQuery = `${beforeSummarize} and StartEffectiveDate < datetime('${contentRefreshDate}') ${summarizeAndAfter}`;
            }
            
            return {
              ...criterion,
              query: updatedQuery
            };
          }
          return criterion;
        });

        // Build the updated policy data with publish information
        const publishedPolicyData = {
          ...currentPolicyData,
          status: "Published",
          isTestPolicy: isInternalTestOnly,
          targetScope: {
            ...currentPolicyData.targetScope,
            contentRefreshDate,
            criteria: updatedCriteria,
          },
          agreementPayload: {
            customerId: "$.Agreement.CustomerId",
            startEffectiveDate: agreementEffectiveDate,
            endEffectiveDate: "9999-12-31T00:00:00Z",
            languageLocale: "$.Agreement.LanguageLocale",
            templateId: currentPolicyData.targetScope?.templateId,
            templateName: currentPolicyData.targetScope?.templateName,
            acceptanceMode: "Implicit",
            assemblyOptions: {
              DocumentAssemblyDate: contentRefreshDate,
              Delta: isDelta
            },
            constraints: constraintsObject,
            participants: [
              {
                id: "100c5d5f-5205-4072-bba4-27bd5635f7e4",
                firstName: "Implicit",
                lastName: "Acceptance",
                Role: "Signer",
                Type: "Microsoft",
                Status: "Accepted",
                StatusDate: agreementEffectiveDate,
                PropertyBag: {},
                SigningOrder: 1
              }
            ]
          },
          execution: {
            jobStartDate: agreementEffectiveDate,
            safeRunPercentage: 5,
            safeRunWaitHours: 24
          },
          modifiedDate: currentDate,
          modifiedBy: currentUser
        };

        const jsonContent = JSON.stringify(publishedPolicyData, null, 2);

        // Get source branch for creating/updating policy branch
        const sourceBranch = policyBranchExists ? policyFeatureBranch : 'main';
        const commitResult = await getLatestCommitFromMain(fetchWithBQ, sourceBranch);

        if (commitResult.error) {
          return { error: commitResult.error };
        }

        const latestCommitId = commitResult.data!;

        // Update the file in Azure DevOps using the Push API
        const publishPushUrl = "pushes?api-version=7.0";
        const publishPushBody = {
          refUpdates: [
            {
              name: `refs/heads/${policyFeatureBranch}`,
              oldObjectId: latestCommitId
            }
          ],
          commits: [
            {
              comment: `Publish policy: ${publishedPolicyData.name || id}`,
              changes: [
                {
                  changeType: "edit",
                  item: {
                    path: filePath
                  },
                  newContent: {
                    content: jsonContent,
                    contentType: "rawtext"
                  }
                }
              ]
            }
          ]
        };

        const publishResult = await fetchWithBQ({
          url: publishPushUrl,
          method: "POST",
          body: publishPushBody
        });


        if (publishResult.error) {
          return { error: publishResult.error };
        }

        // Check if a PR already exists for this branch
        let pullRequestId = await getActivePR(fetchWithBQ, policyFeatureBranch, 'main');
        
        if (pullRequestId) {
          // Use existing PR
        } else {
          // Create a Pull Request to merge to main
          const prResult = await createPullRequest(
            fetchWithBQ,
            policyFeatureBranch,
            `Publish policy: ${publishedPolicyData.name || id}`,
            `This PR publishes the policy "${publishedPolicyData.name || id}".\n\nPolicy ID: ${id}\nContent Refresh Date: ${contentRefreshDate}\nAgreement Effective Date: ${agreementEffectiveDate}\nAssembly Type: ${isDelta ? 'Delta (Updated Terms Only)' : 'Full Agreement'}`,
            'main',
            false
          );

          if (prResult.error) {
            return { error: prResult.error };
          }

          pullRequestId = prResult.data.pullRequestId as number;
        }

        // Enable auto-complete on the PR to merge to main
        const autoCompleteResult = await enablePullRequestAutoComplete(
          fetchWithBQ,
          pullRequestId,
          currentUserId,
          `Publish policy: ${publishedPolicyData.name || id}`,
          true,
          'squash'
        );

        if (autoCompleteResult.error) {
          // Don't fail the whole operation if auto-complete fails
        }

        const responseData = {
          success: true,
          policyId: id,
          pullRequestId
        };
        return {
          data: responseData
        };
      },
      invalidatesTags: ["PolicyList"],
    }),

    withdrawPolicy: builder.mutation<
      { success: boolean; policyId: string; pullRequestId: number },
      {
        id: string;
        status: string;
        cloud?: string;
        environment?: string;
        policyType?: string;
      }
    >({
      queryFn: async (args, _queryApi, _extraOptions, fetchWithBQ) => {
        const {
          id,
          status,
          cloud,
          environment,
          policyType
        } = args;

        // Only allow withdrawal if status is NOT Draft
        if (status === RevisionStatus.Draft) {
          return {
            error: {
              status: 400,
              data: 'Cannot withdraw policies that are already in Draft status'
            } as any
          };
        }

        // Get the current logged-in user
        const authClient = AuthClientProvider.getInstance();
        const currentUser = authClient.getCurrentUser();
        
        // Get Azure DevOps user ID for PR auto-complete
        const userIdResult = await getCurrentUserAzureDevOpsId(fetchWithBQ);
        
        if (userIdResult.error) {
          return { error: userIdResult.error };
        }
        
        const currentUserId = userIdResult.data!;

        // Construct the file path
        const normalizedPolicyType = policyType?.replace(/\s+/g, '');
        const actualCloud = cloud || environmentConfig.cloud;
        const actualEnvironment = environment || environmentConfig.environment;
        const scopePath = `/${actualCloud}/${actualEnvironment}/${normalizedPolicyType}`;
        const fileName = `${identity(id).replace(/\s+/g, '_')}.json`;
        const filePath = `${scopePath}/${fileName}`;

        // Get the policy's feature branch
        const policyFeatureBranch = `policy/${id.replace(/\s+/g, '-').toLowerCase()}`;

        // Check if policy branch exists
        const policyBranchExists = await branchExists(fetchWithBQ, policyFeatureBranch);
        
        // Determine source: try policy branch first, fallback to main
        let currentPolicyResult = await fetchWithBQ(
          `items?path=${filePath}&versionDescriptor.version=${policyFeatureBranch}&versionDescriptor.versionType=branch&api-version=7.0`
        );

        // If policy branch doesn't exist, get from main
        if (currentPolicyResult.error) {
          currentPolicyResult = await fetchWithBQ(
            `items?path=${filePath}&versionDescriptor.version=main&versionDescriptor.versionType=branch&api-version=7.0`
          );
          
          if (currentPolicyResult.error) {
            return { error: currentPolicyResult.error };
          }
        }

        const currentPolicyData = currentPolicyResult.data as any;

        // Update the policy status to Draft for withdrawal
        // Keep all publish-specific fields so they can be pre-populated when re-publishing
        const withdrawnPolicyData = {
          ...currentPolicyData,
          status: RevisionStatus.Draft,
          modifiedDate: new Date().toISOString(),
          modifiedBy: currentUser
        };

        const jsonContent = JSON.stringify(withdrawnPolicyData, null, 2);

        // Get source branch for creating/updating policy branch
        const sourceBranch = policyBranchExists ? policyFeatureBranch : 'main';
        const commitResult = await getLatestCommitFromMain(fetchWithBQ, sourceBranch);

        if (commitResult.error) {
          return { error: commitResult.error };
        }

        const latestCommitId = commitResult.data!;

        // Commit withdrawal to policy branch (create if doesn't exist)
        const withdrawResult = await fetchWithBQ({
          url: "pushes?api-version=7.0",
          method: "POST",
          body: {
            refUpdates: [
              {
                name: `refs/heads/${policyFeatureBranch}`,
                oldObjectId: latestCommitId
              }
            ],
            commits: [
              {
                comment: `Withdraw policy: ${currentPolicyData.name || id} - Set status to Draft`,
                changes: [
                  {
                    changeType: "edit",
                    item: {
                      path: filePath
                    },
                    newContent: {
                      content: jsonContent,
                      contentType: "rawtext"
                    }
                  }
                ]
              }
            ]
          }
        });


        if (withdrawResult.error) {
          return { error: withdrawResult.error };
        }

        // Check if a PR already exists for this branch
        let pullRequestId = await getActivePR(fetchWithBQ, policyFeatureBranch, 'main');
        
        if (pullRequestId) {
          // Use existing PR
        } else {
          // Create a Pull Request for withdrawal to merge to main
          const prResult = await createPullRequest(
            fetchWithBQ,
            policyFeatureBranch,
            `Withdraw policy: ${currentPolicyData.name || id}`,
            `This PR withdraws the policy "${currentPolicyData.name || id}" by setting its status to Draft.\n\nPolicy ID: ${id}\nPrevious Status: ${status}`,
            'main',
            false
          );

          if (prResult.error) {
            return { error: prResult.error };
          }

          pullRequestId = prResult.data.pullRequestId as number;
        }

        // Enable auto-complete on the PR to merge to main
        const autoCompleteResult = await enablePullRequestAutoComplete(
          fetchWithBQ,
          pullRequestId,
          currentUserId,
          `Withdraw policy: ${currentPolicyData.name || id}`,
          true,
          'squash'
        );

        if (autoCompleteResult.error) {
          // Don't fail the whole operation if auto-complete fails
        }

        const responseData = {
          success: true,
          policyId: id,
          pullRequestId
        };
        return {
          data: responseData
        };
      },
      invalidatesTags: ["PolicyList"],
    }),
  }),
});

export const {
  useGetAllQuery: useGetAllPoliciesQuery,
  useGetPolicyQuery,
  useGetPolicyDetailsQuery,
  useCreatePolicyMutation,
  useUpdatePolicyMutation,
  useUpdatePolicyCriteriaMutation,
  useDeletePolicyMutation,
  usePublishPolicyMutation,
  useWithdrawPolicyMutation,
} = policyApi;
